import React, { useEffect, useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import { api, fetchCompanyOptions } from '../lib/api.js';
import { Modal } from './Modal.js';
import { InlinePersonnelForm, Personnel } from './InlinePersonnelForm.js';
import { AsyncSearchableSelect } from './AsyncSearchableSelect.js';
import { generateSmartShortCodeCandidates } from '@gng-ehs/shared';

export interface CompanyOption {
  id: string;
  short_code: string;
  company_name: string;
  occupier_name?: string;
  factory_address?: string;
  license_number?: string;
  registration_number?: string;
  contact_email?: string;
  communication_email?: string;
}

export interface InspectorOption {
  id: string;
  full_name: string;
  designation?: string;
  role?: string;
}

export const COMMON_VESSEL_TYPES = [
  'Vertical Air Receiver',
  'Horizontal Air Receiver',
  'Air Receiver',
  'Reaction Vessel',
  'Storage Vessel',
  'Steam Jacketed Kettle',
  'Autoclave',
  'Heat Exchanger',
  'Hydro Pneumatic Tank',
  'Ammonia Receiver Tank',
];

export const CONDITION_OPTIONS = ['Good', 'Not Good', 'Not Applicable'];

export interface ParsedForm11Row {
  id: string; // internal UI key
  // Company
  company_input: string; // raw excel value
  company_id: string;
  has_multiple_company_matches?: boolean;
  matching_companies_count?: number;
  matching_company_names?: string[];

  // Vessel Specs & Identification
  vessel_input: string; // raw excel vessel name/type
  vessel_name_description: string;
  vessel_serial_no: string;
  vessel_capacity: string;
  vessel_model_no?: string;
  vessel_location: string;
  vessel_description?: string;

  // Manufacturer & Construction
  manufacturer_name_address: string;
  nature_of_process: string;
  date_of_construction: string;
  thickness_shell: string;
  thickness_dish: string;
  thickness_top_dish?: string;
  thickness_bottom_dish?: string;
  first_taken_into_use_date: string;

  // Pressure & Testing
  safe_working_pressure_recommended: string;
  safe_working_pressure_recommended_after: string;
  hydro_test_pressure: string;

  // Inspector & Examination Date
  personnel_input: string; // raw excel inspector value
  personnel_id: string; // resolved competent person ID
  examination_date: string; // YYYY-MM-DD

  // Conditions & Observations
  condition_vessel: string;
  condition_piping: string;
  fittings_pressure_gauges: string;
  fittings_safety_valve: string;
  fittings_stop_valve: string;
  fittings_reducing_valve: string;
  fittings_additional_safety_valve: string;
  fittings_other_devices: string;
  other_observations: string;
  repairs_required: string;
  repairs_period: string;
  repairs_other_condition: string;
  reduced_working_pressure_pending_repairs: string;
  safe_working_pressure_subrule_8: string;

  // Previous & Findings Defaults
  last_external_exam_date?: string;
  last_internal_exam_date?: string;
  last_hydraulic_exam_details?: string;
  last_ultrasonic_exam_date?: string;
  lagging_removed_status?: string;
  exam_external_findings?: string;
  exam_internal_findings?: string;
  exam_hydraulic_findings?: string;
  exam_ultrasonic_findings?: string;

  // Validation Status
  status: 'VALID' | 'MISSING_COMPANY' | 'MISSING_INSPECTOR' | 'MISSING_VESSEL' | 'INVALID';
  status_message: string;
}

// Generate serial code from vessel name words (e.g. "Vertical Air Receiver" -> "VART/01")
export function computeDefaultVesselSerial(vesselName: string, index = 1): string {
  const clean = (vesselName || '').trim();
  if (!clean) return `VART/${String(index).padStart(2, '0')}`;
  const lower = clean.toLowerCase();
  if (lower.includes('vertical air') || lower.startsWith('var') || lower.startsWith('vart')) {
    return `VART/${String(index).padStart(2, '0')}`;
  }
  if (lower.includes('horizontal air') || lower.startsWith('har') || lower.startsWith('hart')) {
    return `HART/${String(index).padStart(2, '0')}`;
  }
  const words = clean.split(/\s+/).filter(Boolean);
  const initials = words.map((w) => w.charAt(0).toUpperCase()).join('');
  return `${initials || 'PV'}/${String(index).padStart(2, '0')}`;
}

// Clean pressure string to standardized "X Kg/cm²" format
export function formatPressureValue(raw: string | number | undefined, defaultVal = '6 Kg/cm²'): string {
  if (raw === undefined || raw === null || raw === '') return defaultVal;
  const str = String(raw).trim();
  const num = parseFloat(str.replace(/[^\d.]/g, ''));
  if (isNaN(num) || num <= 0) return str || defaultVal;
  return `${num} Kg/cm²`;
}

// Calculate Hydro Test pressure (1.5x SWP)
export function computeHydroPressure(swpStr: string): string {
  const num = parseFloat(swpStr.replace(/[^\d.]/g, ''));
  if (isNaN(num) || num <= 0) return '9 Kg/cm²';
  const hydroNum = (num * 1.5).toFixed(num % 1 === 0 ? 1 : 2).replace(/\.0$/, '');
  return `${hydroNum} Kg/cm²`;
}

export function generateForm11ExcelTemplate(
  companies: CompanyOption[],
  inspectors: InspectorOption[],
  vesselTypes: readonly string[] = COMMON_VESSEL_TYPES
) {
  const wb = XLSX.utils.book_new();

  // Sheet 1: Data Entry Template
  const templateHeaders = [
    'Company (Short Code or Name) *',
    'Vessel Name / Description *',
    'Vessel Tag / Serial No. (Optional)',
    'Capacity (Optional)',
    'Location in Factory (Optional)',
    'Safe Working Pressure (SWP in Kg/cm²) *',
    'Hydro Test Pressure (in Kg/cm²) (Optional)',
    'Competent Person / Inspector Name *',
    'Examination Date (DD-MM-YYYY) *',
    'Make / Manufacturer (Optional)',
    'Date or Year of Construction (Optional)',
    'First Use Date or Year (Optional)',
    'Shell Thickness (Optional)',
    'Dish Thickness (Optional)',
    'Vessel Condition (Good / Not Good / Not Applicable)',
    'Piping Condition (Good / Not Good / Not Applicable)',
    'Safety Valve Condition (Good / Not Good / Not Applicable)',
    'Pressure Gauge Condition (Good / Not Good / Not Applicable)',
    'Other Observations (Optional)',
  ];

  const wsData = [templateHeaders];
  const ws1 = XLSX.utils.aoa_to_sheet(wsData);

  // Set column widths
  ws1['!cols'] = [
    { wch: 35 }, // Company
    { wch: 32 }, // Vessel Name
    { wch: 26 }, // Vessel Tag / Serial No
    { wch: 20 }, // Capacity
    { wch: 28 }, // Location
    { wch: 28 }, // SWP
    { wch: 28 }, // Hydro Pressure
    { wch: 32 }, // Competent Person
    { wch: 26 }, // Exam Date
    { wch: 22 }, // Make
    { wch: 24 }, // Date of Construction
    { wch: 24 }, // First Use
    { wch: 18 }, // Shell Thickness
    { wch: 18 }, // Dish Thickness
    { wch: 22 }, // Vessel Condition
    { wch: 22 }, // Piping Condition
    { wch: 24 }, // Safety Valve Condition
    { wch: 24 }, // Pressure Gauge Condition
    { wch: 45 }, // Other Observations
  ];

  // Sheet 2: Lookups & Instructions
  const lookupHeaders = [
    'Company Short Code',
    'Company Full Name',
    ' ',
    'Common Vessel Types',
    ' ',
    'Competent Person / Inspector Name',
    ' ',
    'Condition Options',
    ' ',
    'Sample Working Pressures',
  ];

  const samplePressures = ['6 Kg/cm²', '7 Kg/cm²', '8 Kg/cm²', '9 Kg/cm²', '10 Kg/cm²', '12 Kg/cm²'];

  const lookupRows: (string | number)[][] = [];
  const maxLen = Math.max(companies.length, vesselTypes.length, inspectors.length, CONDITION_OPTIONS.length, samplePressures.length, 10);
  for (let i = 0; i < maxLen; i++) {
    const c = companies[i];
    const v = vesselTypes[i];
    const ins = inspectors[i];
    const cond = CONDITION_OPTIONS[i];
    const p = samplePressures[i];
    lookupRows.push([
      c ? c.short_code : '',
      c ? c.company_name : '',
      ' ',
      v || '',
      ' ',
      ins ? ins.full_name : '',
      ' ',
      cond || '',
      ' ',
      p || '',
    ]);
  }

  const instructions = [
    ['INSTRUCTIONS & GUIDE FOR BULK IMPORTING FORM 11 PRESSURE VESSEL REPORTS:'],
    ['1. FILLING COMPANY:'],
    ['   - Enter Company Short Code (e.g. SPS) or Company Name in Column A.'],
    ['   - Or click cell in Column A to select from available companies in the dropdown.'],
    ['2. VESSEL NAME / DESCRIPTION:'],
    ['   - Enter Vessel Name (e.g. "Vertical Air Receiver", "Horizontal Air Receiver", etc.) in Column B.'],
    ['3. VESSEL TAG / SERIAL NUMBER (OPTIONAL):'],
    ['   - If left blank, the system will automatically generate standard sequential tag codes (e.g. VART/01, HART/01).'],
    ['4. SAFE WORKING PRESSURE (SWP):'],
    ['   - Enter SWP e.g. "6", "6 Kg/cm²", "7", etc. in Column F.'],
    ['5. HYDRO TEST PRESSURE (OPTIONAL):'],
    ['   - If left blank, automatically calculated as 1.5x Working Pressure (e.g. 6 Kg/cm² -> 9 Kg/cm²).'],
    ['6. COMPETENT PERSON / INSPECTOR:'],
    ['   - Select or enter the Competent Person full name in Column H.'],
    ['7. EXAMINATION DATE:'],
    ['   - Standard DD-MM-YYYY (e.g. 15-08-2026) or standard Excel date format in Column I.'],
    ['8. CONDITIONS (OPTIONAL):'],
    ['   - Select "Good", "Not Good", or "Not Applicable" (defaults to "Good" if blank).'],
    [''],
    ['--- CURRENT SYSTEM LOOKUP DATA ---'],
    lookupHeaders,
    ...lookupRows,
  ];

  const ws2 = XLSX.utils.aoa_to_sheet(instructions);
  ws2['!cols'] = [{ wch: 22 }, { wch: 35 }, { wch: 4 }, { wch: 30 }, { wch: 4 }, { wch: 32 }, { wch: 4 }, { wch: 22 }, { wch: 4 }, { wch: 24 }];

  // SheetJS Data Validation lists for native Excel dropdowns
  const companyCount = Math.max(companies.length, 1);
  const vesselCount = Math.max(vesselTypes.length, 1);
  const inspectorCount = Math.max(inspectors.length, 1);

  ws1['!dataValidation'] = [
    {
      sqref: 'A2:A500', // Company
      type: 'list',
      operator: 'equal',
      formula1: `'Lookups'!$A$15:$A$${14 + companyCount}`,
      allowBlank: true,
    },
    {
      sqref: 'B2:B500', // Vessel Name
      type: 'list',
      operator: 'equal',
      formula1: `'Lookups'!$D$15:$D$${14 + vesselCount}`,
      allowBlank: true,
    },
    {
      sqref: 'H2:H500', // Competent Person
      type: 'list',
      operator: 'equal',
      formula1: `'Lookups'!$F$15:$F$${14 + inspectorCount}`,
      allowBlank: true,
    },
    {
      sqref: 'O2:O500', // Vessel Condition
      type: 'list',
      operator: 'equal',
      formula1: `'Lookups'!$H$15:$H$17`,
      allowBlank: true,
    },
    {
      sqref: 'P2:P500', // Piping Condition
      type: 'list',
      operator: 'equal',
      formula1: `'Lookups'!$H$15:$H$17`,
      allowBlank: true,
    },
    {
      sqref: 'Q2:Q500', // Safety Valve Condition
      type: 'list',
      operator: 'equal',
      formula1: `'Lookups'!$H$15:$H$17`,
      allowBlank: true,
    },
    {
      sqref: 'R2:R500', // Pressure Gauge Condition
      type: 'list',
      operator: 'equal',
      formula1: `'Lookups'!$H$15:$H$17`,
      allowBlank: true,
    },
  ];

  XLSX.utils.book_append_sheet(wb, ws1, 'Form 11 Template');
  XLSX.utils.book_append_sheet(wb, ws2, 'Lookups');

  XLSX.writeFile(wb, `Form_11_Pressure_Vessels_Import_Template.xlsx`);
}

export function Form11ExcelImportModal({
  open,
  onClose,
  onSuccess,
}: {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [companies, setCompanies] = useState<CompanyOption[]>([]);
  const [inspectors, setInspectors] = useState<InspectorOption[]>([]);
  const [loadingLookups, setLoadingLookups] = useState(false);

  const [parsedRows, setParsedRows] = useState<ParsedForm11Row[]>([]);
  const [fileName, setFileName] = useState('');
  const [parsing, setParsing] = useState(false);

  // Quick Add Company panel state
  const [showQuickCompanyForm, setShowQuickCompanyForm] = useState(false);
  const [activeCompanyTargetRowId, setActiveCompanyTargetRowId] = useState<string | null>(null);
  const [quickCompanyName, setQuickCompanyName] = useState('');
  const [quickCompanySaving, setQuickCompanySaving] = useState(false);

  // Inline Personnel Form state for adding new Inspector
  const [showInlineInspectorForm, setShowInlineInspectorForm] = useState(false);
  const [activeInspectorTargetRowId, setActiveInspectorTargetRowId] = useState<string | null>(null);
  const [activeInspectorInitialName, setActiveInspectorInitialName] = useState<string>('');

  // Batch actions state
  const [batchCompanyId, setBatchCompanyId] = useState('');
  const [batchVesselName, setBatchVesselName] = useState('Vertical Air Receiver');
  const [batchPersonnelId, setBatchPersonnelId] = useState('');
  const [batchDate, setBatchDate] = useState('');
  const [batchPressure, setBatchPressure] = useState('6');
  const [batchCondition, setBatchCondition] = useState('Good');

  // Import execution state
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState<{ current: number; total: number; step: string } | null>(null);
  const [error, setError] = useState('');
  const [importSuccessSummary, setImportSuccessSummary] = useState<{ count: number } | null>(null);

  // Load companies & competent persons on open
  useEffect(() => {
    if (!open) return;
    setLoadingLookups(true);

    Promise.all([
      api.companies.list({ limit: 1000 }),
      api.personnel.list({ limit: 1000 }),
    ])
      .then(([compRes, persRes]) => {
        const compList = (compRes.data ?? []) as CompanyOption[];
        setCompanies(compList);

        const persList = (persRes.data ?? []) as InspectorOption[];
        setInspectors(persList);
      })
      .catch((err) => {
        console.error('Failed to load lookup data for Form 11:', err);
      })
      .finally(() => {
        setLoadingLookups(false);
      });
  }, [open]);

  // Robust Excel date parser
  const normalizeDateStr = (raw: unknown): string => {
    if (raw === undefined || raw === null || raw === '') return new Date().toISOString().split('T')[0];

    const numVal = typeof raw === 'number' ? raw : (typeof raw === 'string' && /^\d{5}(\.\d+)?$/.test(raw.trim()) ? Number(raw.trim()) : NaN);
    if (!isNaN(numVal) && numVal > 25000 && numVal < 75000) {
      const parsedDate = XLSX.SSF.parse_date_code(numVal);
      if (parsedDate) {
        const yyyy = String(parsedDate.y).padStart(4, '0');
        const mm = String(parsedDate.m).padStart(2, '0');
        const dd = String(parsedDate.d).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
      }
    }

    const s = String(raw).trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;

    const ddmmyyyy = s.match(/^(\d{1,2})[/\-.]( \d{1,2})[/\-.]( \d{4})$/);
    if (ddmmyyyy) {
      const dd = String(ddmmyyyy[1]).padStart(2, '0');
      const mm = String(ddmmyyyy[2]).padStart(2, '0');
      const yyyy = ddmmyyyy[3];
      return `${yyyy}-${mm}-${dd}`;
    }

    const yyyymmdd = s.match(/^(\d{4})[/\-.]( \d{1,2})[/\-.]( \d{1,2})$/);
    if (yyyymmdd) {
      const yyyy = yyyymmdd[1];
      const mm = String(yyyymmdd[2]).padStart(2, '0');
      const dd = String(yyyymmdd[3]).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    }

    const d = new Date(s);
    if (!isNaN(d.getTime())) {
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    }

    return new Date().toISOString().split('T')[0];
  };

  // Helper entity matcher
  const matchRowData = (
    rawCompany: string,
    rawVessel: string,
    rawInspector: string,
    allCompanies: CompanyOption[],
    allInspectors: InspectorOption[]
  ) => {
    // 1. Match Company
    const compClean = rawCompany.trim();
    let company_id = '';
    let has_multiple_company_matches = false;
    let matching_companies_count = 0;
    let matching_company_names: string[] = [];

    if (compClean) {
      const compCleanLower = compClean.toLowerCase();

      // Strong / Direct matches (exact code, exact name, or standard composite formats)
      const exactMatches = allCompanies.filter((c) => {
        const cName = (c.company_name || '').trim().toLowerCase();
        const cCode = (c.short_code || '').trim().toLowerCase();
        return (
          (cCode && cCode === compCleanLower) ||
          (cName && cName === compCleanLower) ||
          (cCode && cName && `${cCode} - ${cName}` === compCleanLower) ||
          (cCode && cName && `${cName} - ${cCode}` === compCleanLower) ||
          (cCode && cName && `${cName} [${cCode}]` === compCleanLower) ||
          (cCode && cName && `${cCode} [${cName}]` === compCleanLower) ||
          (cCode && compCleanLower.startsWith(`${cCode} -`)) ||
          (cName && compCleanLower.startsWith(`${cName} -`)) ||
          (cName && compCleanLower.startsWith(`${cName} [`)) ||
          (cCode && compCleanLower.startsWith(`${cCode} [`))
        );
      });

      let compMatches = exactMatches;

      // Fallback: If no exact/strong match, check substring / contains match
      if (compMatches.length === 0) {
        compMatches = allCompanies.filter((c) => {
          const cName = (c.company_name || '').trim().toLowerCase();
          const cCode = (c.short_code || '').trim().toLowerCase();
          return (
            (cName && (cName.includes(compCleanLower) || (compCleanLower.length >= 3 && compCleanLower.includes(cName)))) ||
            (cCode && cCode.includes(compCleanLower))
          );
        });
      }

      if (compMatches.length > 0) {
        company_id = compMatches[0].id;
        if (compMatches.length > 1) {
          has_multiple_company_matches = true;
          matching_companies_count = compMatches.length;
          matching_company_names = compMatches.map((m) =>
            m.short_code ? `${m.short_code} - ${m.company_name}` : m.company_name
          );
        }
      }
    }

    // 2. Match Competent Person / Inspector
    const insClean = rawInspector.trim();
    let personnel_id = '';
    if (insClean) {
      const foundIns = allInspectors.find(
        (ins) =>
          ins.full_name.toLowerCase() === insClean.toLowerCase() ||
          insClean.toLowerCase().includes(ins.full_name.toLowerCase()) ||
          ins.full_name.toLowerCase().includes(insClean.toLowerCase())
      );
      if (foundIns) {
        personnel_id = foundIns.id;
      }
    }

    // 3. Resolve Vessel Name / Description
    const vesselName = rawVessel.trim() || 'Vertical Air Receiver';

    return {
      company_id,
      has_multiple_company_matches,
      matching_companies_count,
      matching_company_names,
      vesselName,
      personnel_id,
    };
  };

  // File Upload Handler
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setParsing(true);
    setError('');

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const bstr = evt.target?.result;
        const wb = XLSX.read(bstr, { type: 'binary' });

        const sheetName =
          wb.SheetNames.find((n) => n.includes('Template') || n.includes('Form 11') || n.includes('Pressure') || n.includes('Sheet1')) ||
          wb.SheetNames[0];
        const ws = wb.Sheets[sheetName];

        const rawData = XLSX.utils.sheet_to_json<Record<string, unknown>>(ws, { defval: '' });

        if (rawData.length === 0) {
          setError('The uploaded Excel file contains no data rows.');
          setParsing(false);
          return;
        }

        const rows: ParsedForm11Row[] = [];

        rawData.forEach((rowObj, index) => {
          const keys = Object.keys(rowObj);
          const getRawVal = (matches: string[]) => {
            const k = keys.find((key) => matches.some((m) => key.toLowerCase().trim().includes(m.toLowerCase())));
            return k ? rowObj[k] : undefined;
          };
          const getKeyVal = (matches: string[]) => {
            const val = getRawVal(matches);
            return val !== undefined && val !== null ? String(val).trim() : '';
          };

          const company_input = getKeyVal(['company', 'client', 'organisation']);
          const vessel_input = getKeyVal(['vessel name', 'vessel', 'description', 'plant name', 'equipment name']);
          const raw_serial_no = getKeyVal(['vessel serial', 'serial no', 'tag no', 'tag name', 'serial', 'tag', 'identification']);
          const vessel_capacity = getKeyVal(['capacity', 'vessel capacity', 'volume', 'size']) || '1 KL';
          const vessel_model_no = getKeyVal(['model no', 'model', 'vessel model']);
          const vessel_location = getKeyVal(['location in factory', 'location', 'factory location', 'vessel location']) || 'Near Compressor Room';
          const vessel_description = getKeyVal(['vessel description', 'additional description', 'vessel remarks', 'additional info', 'extra description']) || '';

          const manufacturer_name_address = getKeyVal(['make', 'manufacturer', 'mfg']) || '';
          const date_of_construction_raw = getKeyVal(['date of construction', 'construction date', 'year of construction', 'mfg year', 'built year']);
          const date_of_construction = date_of_construction_raw ? normalizeDateStr(date_of_construction_raw) : '1992-01-01';

          const first_use_raw = getKeyVal(['first use', 'first taken into use', 'first used', 'use date']);
          const first_taken_into_use_date = first_use_raw ? normalizeDateStr(first_use_raw) : date_of_construction;

          const thickness_shell = getKeyVal(['shell thickness', 'shell', 'thickness shell']) || '5.5 mm';
          const thickness_dish = getKeyVal(['dish thickness', 'dish', 'thickness dish']) || '8 mm';
          const thickness_top_dish = getKeyVal(['top dish', 'top dish thickness']);
          const thickness_bottom_dish = getKeyVal(['bottom dish', 'bottom dish thickness']);

          const rawSwp = getKeyVal(['safe working pressure', 'swp', 'working pressure', 'pressure']);
          const safe_working_pressure_recommended = formatPressureValue(rawSwp, '6 Kg/cm²');
          const safe_working_pressure_recommended_after = safe_working_pressure_recommended;

          const rawHydro = getKeyVal(['hydro test pressure', 'hydro pressure', 'hydraulic pressure', 'hydro test']);
          const hydro_test_pressure = rawHydro ? formatPressureValue(rawHydro, computeHydroPressure(safe_working_pressure_recommended)) : computeHydroPressure(safe_working_pressure_recommended);

          const personnel_input = getKeyVal(['competent person', 'inspector', 'personnel', 'examiner']);
          const rawDate = getRawVal(['examination date', 'date of exam', 'exam date', 'test date', 'date']);
          const examination_date = normalizeDateStr(rawDate);

          const nature_of_process = getKeyVal(['nature of process', 'process']) || `For storage and supply of air at ${safe_working_pressure_recommended}`;

          const condition_vessel = getKeyVal(['vessel condition', 'condition vessel', 'tank condition']) || 'Good';
          const condition_piping = getKeyVal(['piping condition', 'condition piping', 'pipe condition']) || 'Good';
          const fittings_safety_valve = getKeyVal(['safety valve condition', 'safety valve', 'safety valve fitting']) || 'Good';
          const fittings_pressure_gauges = getKeyVal(['pressure gauge condition', 'pressure gauge', 'gauge condition']) || 'Good';
          const fittings_stop_valve = getKeyVal(['stop valve condition', 'stop valve']) || 'Good';
          const fittings_reducing_valve = getKeyVal(['reducing valve condition', 'reducing valve']) || 'Not Applicable';
          const fittings_additional_safety_valve = getKeyVal(['additional safety valve']) || 'Not Applicable';
          const fittings_other_devices = getKeyVal(['other devices', 'fittings other']) || 'Not Applicable';

          const other_observations = getKeyVal(['other observations', 'observations', 'remarks']) || 'Safety valve operation and setting to be checked regularly.';
          const repairs_required = getKeyVal(['repairs required', 'repairs']) || 'NIL';
          const repairs_period = getKeyVal(['repairs period']) || 'Not Applicable';
          const repairs_other_condition = getKeyVal(['repairs other condition']) || 'NIL';
          const reduced_working_pressure_pending_repairs = getKeyVal(['reduced working pressure']) || 'Not Required';
          const safe_working_pressure_subrule_8 = getKeyVal(['subrule 8 pressure', 'subrule 8']) || 'Not Applicable';

          // Skip completely blank rows
          if (!company_input && !vessel_input && rawDate === undefined) return;

          const matched = matchRowData(company_input, vessel_input, personnel_input, companies, inspectors);
          const finalVesselName = matched.vesselName || 'Vertical Air Receiver';
          const vessel_serial_no = raw_serial_no || computeDefaultVesselSerial(finalVesselName, index + 1);

          let status: 'VALID' | 'MISSING_COMPANY' | 'MISSING_INSPECTOR' | 'MISSING_VESSEL' | 'INVALID' = 'VALID';
          let status_message = 'Ready to import';

          if (!matched.company_id) {
            status = 'MISSING_COMPANY';
            status_message = company_input ? `Unmatched Company "${company_input}"` : 'Missing Company';
          } else if (!matched.personnel_id) {
            status = 'MISSING_INSPECTOR';
            status_message = personnel_input ? `Unmatched Inspector "${personnel_input}"` : 'Missing Competent Person';
          } else if (!finalVesselName) {
            status = 'MISSING_VESSEL';
            status_message = 'Missing Vessel Description';
          } else if (!examination_date) {
            status = 'INVALID';
            status_message = 'Missing Examination Date';
          }

          rows.push({
            id: `row_${index}_${Math.random().toString(36).substring(2, 7)}`,
            company_input,
            company_id: matched.company_id,
            has_multiple_company_matches: matched.has_multiple_company_matches,
            matching_companies_count: matched.matching_companies_count,
            matching_company_names: matched.matching_company_names,
            vessel_input,
            vessel_name_description: finalVesselName,
            vessel_serial_no,
            vessel_capacity,
            vessel_model_no,
            vessel_location,
            vessel_description,
            manufacturer_name_address,
            nature_of_process,
            date_of_construction,
            thickness_shell,
            thickness_dish,
            thickness_top_dish,
            thickness_bottom_dish,
            first_taken_into_use_date,
            safe_working_pressure_recommended,
            safe_working_pressure_recommended_after,
            hydro_test_pressure,
            personnel_input,
            personnel_id: matched.personnel_id,
            examination_date,
            condition_vessel,
            condition_piping,
            fittings_pressure_gauges,
            fittings_safety_valve,
            fittings_stop_valve,
            fittings_reducing_valve,
            fittings_additional_safety_valve,
            fittings_other_devices,
            other_observations,
            repairs_required,
            repairs_period,
            repairs_other_condition,
            reduced_working_pressure_pending_repairs,
            safe_working_pressure_subrule_8,
            status,
            status_message,
          });
        });

        if (rows.length === 0) {
          setError('Could not find valid certificate rows in the file. Please use our downloadable sample Excel template.');
        } else {
          setParsedRows(rows);
        }
      } catch (err: unknown) {
        setError(`Failed to parse Excel file: ${err instanceof Error ? err.message : 'Unknown format'}`);
      } finally {
        setParsing(false);
      }
    };

    reader.readAsBinaryString(file);
  };

  // Row update with smart propagation for identical missing inputs
  const updateRow = (id: string, updates: Partial<ParsedForm11Row>) => {
    setParsedRows((prev) => {
      const targetRow = prev.find((r) => r.id === id);
      const targetCompanyInput = targetRow?.company_input?.trim().toLowerCase();
      const targetPersonnelInput = targetRow?.personnel_input?.trim().toLowerCase();
      const targetVesselInput = targetRow?.vessel_input?.trim().toLowerCase();

      return prev.map((r) => {
        const isTarget = r.id === id;
        const isMatchingSameCompany =
          !isTarget &&
          updates.company_id !== undefined &&
          targetCompanyInput &&
          r.company_input &&
          r.company_input.trim().toLowerCase() === targetCompanyInput;

        const isMatchingSamePersonnel =
          !isTarget &&
          updates.personnel_id !== undefined &&
          targetPersonnelInput &&
          r.personnel_input &&
          r.personnel_input.trim().toLowerCase() === targetPersonnelInput;

        const isMatchingSameVessel =
          !isTarget &&
          updates.vessel_name_description !== undefined &&
          targetVesselInput &&
          r.vessel_input &&
          r.vessel_input.trim().toLowerCase() === targetVesselInput;

        if (!isTarget && !isMatchingSameCompany && !isMatchingSamePersonnel && !isMatchingSameVessel) {
          return r;
        }

        const updated = {
          ...r,
          ...updates,
          ...(updates.company_id !== undefined ? { has_multiple_company_matches: false } : {}),
        };

        // If safe working pressure changed, update hydro test pressure & nature of process
        if (updates.safe_working_pressure_recommended !== undefined) {
          const formattedSwp = formatPressureValue(updates.safe_working_pressure_recommended);
          updated.safe_working_pressure_recommended = formattedSwp;
          updated.safe_working_pressure_recommended_after = formattedSwp;
          if (!r.hydro_test_pressure || r.hydro_test_pressure === computeHydroPressure(r.safe_working_pressure_recommended)) {
            updated.hydro_test_pressure = computeHydroPressure(formattedSwp);
          }
          updated.nature_of_process = `For storage and supply of air at ${formattedSwp}`;
        }

        // Recompute status
        if (!updated.company_id) {
          updated.status = 'MISSING_COMPANY';
          updated.status_message = updated.company_input ? `Unmatched Company "${updated.company_input}"` : 'Missing Company';
        } else if (!updated.personnel_id) {
          updated.status = 'MISSING_INSPECTOR';
          updated.status_message = updated.personnel_input ? `Unmatched Inspector "${updated.personnel_input}"` : 'Missing Competent Person';
        } else if (!updated.vessel_name_description?.trim()) {
          updated.status = 'MISSING_VESSEL';
          updated.status_message = 'Missing Vessel Description';
        } else if (!updated.examination_date) {
          updated.status = 'INVALID';
          updated.status_message = 'Missing Examination Date';
        } else {
          updated.status = 'VALID';
          updated.status_message = 'Ready to import';
        }

        return updated;
      });
    });
  };

  const removeRow = (id: string) => {
    setParsedRows((prev) => prev.filter((r) => r.id !== id));
  };

  // Batch actions
  const applyBatchCompany = () => {
    if (!batchCompanyId) return;
    setParsedRows((prev) =>
      prev.map((r) => {
        const updated = { ...r, company_id: batchCompanyId, has_multiple_company_matches: false };
        if (updated.personnel_id && updated.vessel_name_description && updated.examination_date) {
          updated.status = 'VALID';
          updated.status_message = 'Ready to import';
        }
        return updated;
      })
    );
  };

  const applyBatchVessel = () => {
    if (!batchVesselName) return;
    setParsedRows((prev) =>
      prev.map((r, idx) => {
        const updated = {
          ...r,
          vessel_name_description: batchVesselName,
          vessel_serial_no: r.vessel_serial_no || computeDefaultVesselSerial(batchVesselName, idx + 1),
        };
        if (updated.company_id && updated.personnel_id && updated.examination_date) {
          updated.status = 'VALID';
          updated.status_message = 'Ready to import';
        }
        return updated;
      })
    );
  };

  const applyBatchPersonnel = () => {
    if (!batchPersonnelId) return;
    setParsedRows((prev) =>
      prev.map((r) => {
        const updated = { ...r, personnel_id: batchPersonnelId };
        if (updated.company_id && updated.vessel_name_description && updated.examination_date) {
          updated.status = 'VALID';
          updated.status_message = 'Ready to import';
        }
        return updated;
      })
    );
  };

  const applyBatchDate = () => {
    if (!batchDate) return;
    setParsedRows((prev) =>
      prev.map((r) => ({
        ...r,
        examination_date: batchDate,
      }))
    );
  };

  const applyBatchPressure = () => {
    if (!batchPressure) return;
    const formattedSwp = formatPressureValue(batchPressure);
    const computedHydro = computeHydroPressure(formattedSwp);
    setParsedRows((prev) =>
      prev.map((r) => ({
        ...r,
        safe_working_pressure_recommended: formattedSwp,
        safe_working_pressure_recommended_after: formattedSwp,
        hydro_test_pressure: computedHydro,
        nature_of_process: `For storage and supply of air at ${formattedSwp}`,
      }))
    );
  };

  const applyBatchCondition = () => {
    if (!batchCondition) return;
    setParsedRows((prev) =>
      prev.map((r) => ({
        ...r,
        condition_vessel: batchCondition,
        condition_piping: batchCondition,
        fittings_pressure_gauges: batchCondition,
        fittings_safety_valve: batchCondition,
        fittings_stop_valve: batchCondition,
      }))
    );
  };

  // Quick Add Company Handler
  const handleQuickCreateCompany = async () => {
    const name = quickCompanyName.trim();
    if (!name) {
      setError('Please enter a Company Name.');
      return;
    }

    setQuickCompanySaving(true);
    setError('');

    try {
      let short_code = '';
      try {
        const suggestedRes = await api.companies.suggestShortCode(name);
        short_code = suggestedRes.data?.short_code || '';
      } catch {
        /* fallback to client candidate matching */
      }

      if (!short_code) {
        const candidates = generateSmartShortCodeCandidates(name);
        const existingCodes = new Set(companies.map((c) => c.short_code.toUpperCase()));
        short_code = candidates.find((c) => !existingCodes.has(c.toUpperCase())) || candidates[0] || name.slice(0, 4).toUpperCase();
      }

      const createdRes = await api.companies.create({
        company_name: name,
        short_code,
        occupier_name: 'N/A',
        factory_address: 'N/A',
      });

      const newCompany = (createdRes as { data?: CompanyOption }).data;
      if (newCompany?.id) {
        setCompanies((prev) => [...prev, newCompany]);
        setShowQuickCompanyForm(false);

        const targetRow = parsedRows.find((r) => r.id === activeCompanyTargetRowId);
        const targetInput = (name || targetRow?.company_input || '').trim().toLowerCase();

        setParsedRows((prev) =>
          prev.map((r) => {
            const isMatchingRow =
              r.id === activeCompanyTargetRowId ||
              (targetInput && r.company_input && r.company_input.trim().toLowerCase() === targetInput) ||
              (r.company_input && name.toLowerCase().includes(r.company_input.trim().toLowerCase()));

            if (isMatchingRow) {
              const updated = {
                ...r,
                company_id: newCompany.id,
                company_input: `${newCompany.short_code} - ${newCompany.company_name}`,
                has_multiple_company_matches: false,
              };
              if (updated.personnel_id && updated.vessel_name_description && updated.examination_date) {
                updated.status = 'VALID';
                updated.status_message = 'Ready to import';
              } else if (!updated.personnel_id) {
                updated.status = 'MISSING_INSPECTOR';
                updated.status_message = 'Missing Competent Person';
              }
              return updated;
            }
            return r;
          })
        );
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to create company');
    } finally {
      setQuickCompanySaving(false);
    }
  };

  // Handle Created Personnel / Inspector
  const handleInspectorCreated = (newInspector: Personnel) => {
    setInspectors((prev) => [...prev, newInspector]);
    setShowInlineInspectorForm(false);

    const targetRow = parsedRows.find((r) => r.id === activeInspectorTargetRowId);
    const targetName = (targetRow?.personnel_input || newInspector.full_name).trim().toLowerCase();

    setParsedRows((prev) =>
      prev.map((r) => {
        const isMatchingRow =
          r.id === activeInspectorTargetRowId ||
          (targetName && r.personnel_input && r.personnel_input.trim().toLowerCase() === targetName) ||
          (r.personnel_input && newInspector.full_name.toLowerCase().includes(r.personnel_input.trim().toLowerCase()));

        if (isMatchingRow) {
          const updated = {
            ...r,
            personnel_id: newInspector.id,
            personnel_input: newInspector.full_name,
          };
          if (updated.company_id && updated.vessel_name_description && updated.examination_date) {
            updated.status = 'VALID';
            updated.status_message = 'Ready to import';
          }
          return updated;
        }
        return r;
      })
    );
    setActiveInspectorTargetRowId(null);
  };

  // Perform Final Bulk Import
  const handleExecuteImport = async () => {
    const invalidRows = parsedRows.filter((r) => r.status !== 'VALID');
    if (invalidRows.length > 0) {
      setError(`Cannot proceed: ${invalidRows.length} row(s) have unresolved errors or missing fields.`);
      return;
    }

    setImporting(true);
    setError('');
    setProgress({ current: 0, total: parsedRows.length, step: 'Starting import...' });

    try {
      const createdCount = parsedRows.length;

      // Import row by row: Create Form 11 certificate
      for (let i = 0; i < parsedRows.length; i++) {
        const row = parsedRows[i];
        setProgress({
          current: i + 1,
          total: parsedRows.length,
          step: `Processing certificate ${i + 1} of ${parsedRows.length} (${row.vessel_name_description} — ${row.vessel_serial_no})...`,
        });

        const form11Payload = {
          company_id: row.company_id,
          personnel_id: row.personnel_id,
          examination_date: row.examination_date,

          vessel_name_description: row.vessel_name_description,
          vessel_serial_no: row.vessel_serial_no || computeDefaultVesselSerial(row.vessel_name_description, i + 1),
          vessel_capacity: row.vessel_capacity || '1 KL',
          vessel_model_no: row.vessel_model_no || '',
          vessel_location: row.vessel_location || 'Near Compressor Room',
          vessel_description: row.vessel_description || '',

          manufacturer_name_address: row.manufacturer_name_address || '',
          nature_of_process: row.nature_of_process || `For storage and supply of air at ${row.safe_working_pressure_recommended}`,

          date_of_construction: row.date_of_construction || '1992-01-01',
          thickness_shell: row.thickness_shell || '5.5 mm',
          thickness_dish: row.thickness_dish || '8 mm',
          thickness_top_dish: row.thickness_top_dish || '',
          thickness_bottom_dish: row.thickness_bottom_dish || '',
          first_taken_into_use_date: row.first_taken_into_use_date || row.date_of_construction || '1992-01-01',
          safe_working_pressure_recommended: row.safe_working_pressure_recommended || '6 Kg/cm²',

          last_external_exam_date: row.last_external_exam_date || '',
          last_internal_exam_date: row.last_internal_exam_date || 'Inaccessible',
          last_hydraulic_exam_details: row.last_hydraulic_exam_details || 'First time by us',
          last_ultrasonic_exam_date: row.last_ultrasonic_exam_date || '',

          lagging_removed_status: row.lagging_removed_status || 'Non-Insulated Vessel',

          exam_external_findings: row.exam_external_findings || '',
          exam_internal_findings: row.exam_internal_findings || '(b) Inaccessible',
          exam_hydraulic_findings: row.exam_hydraulic_findings || '',
          hydro_test_pressure: row.hydro_test_pressure || computeHydroPressure(row.safe_working_pressure_recommended),
          exam_ultrasonic_findings: row.exam_ultrasonic_findings || 'Shell: - 5, 5.1, 5 mm, Dish: - 7.7, 7.65 mm',

          condition_vessel: row.condition_vessel || 'Good',
          condition_piping: row.condition_piping || 'Good',

          fittings_pressure_gauges: row.fittings_pressure_gauges || 'Good',
          fittings_safety_valve: row.fittings_safety_valve || 'Good',
          fittings_stop_valve: row.fittings_stop_valve || 'Good',
          fittings_reducing_valve: row.fittings_reducing_valve || 'Not Applicable',
          fittings_additional_safety_valve: row.fittings_additional_safety_valve || 'Not Applicable',
          fittings_other_devices: row.fittings_other_devices || 'Not Applicable',

          safe_working_pressure_recommended_after: row.safe_working_pressure_recommended_after || row.safe_working_pressure_recommended || '6 Kg/cm²',
          repairs_required: row.repairs_required || 'NIL',
          repairs_period: row.repairs_period || 'Not Applicable',
          repairs_other_condition: row.repairs_other_condition || 'NIL',
          reduced_working_pressure_pending_repairs: row.reduced_working_pressure_pending_repairs || 'Not Required',
          safe_working_pressure_subrule_8: row.safe_working_pressure_subrule_8 || 'Not Applicable',
          other_observations: row.other_observations || 'Safety valve operation and setting to be checked regularly.',
        };

        await api.form11.create(form11Payload);
      }

      setImportSuccessSummary({ count: createdCount });
      onSuccess();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Bulk import failed unexpectedly.');
    } finally {
      setImporting(false);
      setProgress(null);
    }
  };

  const handleResetModal = () => {
    setParsedRows([]);
    setFileName('');
    setError('');
    setImportSuccessSummary(null);
    setShowInlineInspectorForm(false);
    setShowQuickCompanyForm(false);
  };

  // Validation Summary & Counts
  const validationSummary = useMemo(() => {
    const missingCompany = parsedRows.filter((r) => !r.company_id).length;
    const missingInspector = parsedRows.filter((r) => !r.personnel_id).length;
    const missingVessel = parsedRows.filter((r) => !r.vessel_name_description?.trim()).length;
    const missingDate = parsedRows.filter((r) => !r.examination_date).length;

    const reasons: string[] = [];
    if (missingCompany > 0) reasons.push(`${missingCompany} row(s) missing company`);
    if (missingInspector > 0) reasons.push(`${missingInspector} row(s) missing competent person`);
    if (missingVessel > 0) reasons.push(`${missingVessel} row(s) missing vessel description`);
    if (missingDate > 0) reasons.push(`${missingDate} row(s) missing exam date`);

    return {
      isValid: parsedRows.length > 0 && reasons.length === 0,
      reason: reasons.join(', '),
      missingCompany,
      missingInspector,
      missingVessel,
      missingDate,
    };
  }, [parsedRows]);

  const multipleMatchRowsCount = useMemo(() => {
    return parsedRows.filter((r) => r.has_multiple_company_matches).length;
  }, [parsedRows]);

  return (
    <Modal
      open={open}
      onClose={() => {
        if (importing) return;
        handleResetModal();
        onClose();
      }}
      title="⚡ Bulk Import Form 11 Pressure Vessel Reports via Excel"
      size="full"
    >
      <div className="space-y-4 text-xs sm:text-sm">
        {/* Top Description & Sample Template Download Button */}
        <div className="p-3.5 bg-brand-50/70 border border-brand-200 rounded-xl flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
          <div>
            <h4 className="font-bold text-brand-900 text-sm">Download Clean Excel Template with Native Dropdowns</h4>
            <p className="text-xs text-brand-700 mt-0.5">
              Download our clean Excel template featuring interactive Excel dropdowns for Companies, Vessel Types, Inspectors, and Condition Options.
            </p>
          </div>
          <button
            type="button"
            onClick={() => generateForm11ExcelTemplate(companies, inspectors)}
            disabled={loadingLookups}
            className="px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white font-semibold text-xs rounded-lg shadow-sm flex items-center gap-1.5 shrink-0 cursor-pointer transition-all"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 5v14M5 12l7 7 7-7" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Download Sample Excel Template
          </button>
        </div>

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-xs rounded-xl flex justify-between items-center">
            <span>{error}</span>
            <button type="button" onClick={() => setError('')} className="text-red-500 hover:text-red-700">✕</button>
          </div>
        )}

        {/* Success Screen */}
        {importSuccessSummary ? (
          <div className="p-6 bg-emerald-50 border border-emerald-200 rounded-2xl text-center space-y-4 my-4">
            <div className="w-12 h-12 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto text-2xl font-bold">
              ✓
            </div>
            <div>
              <h3 className="text-lg font-bold text-emerald-900">Bulk Import Completed Successfully!</h3>
              <p className="text-sm text-emerald-700 mt-1">
                Successfully generated <strong>{importSuccessSummary.count} Form 11 Pressure Vessel Certificates</strong> and hydro-test schedules.
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                handleResetModal();
                onClose();
              }}
              className="btn btn-primary text-xs px-6 py-2 cursor-pointer"
            >
              Close & View Form 11 Certificates
            </button>
          </div>
        ) : (
          <>
            {/* Quick Add Company Panel */}
            {showQuickCompanyForm && (
              <div className="p-4 bg-emerald-50/90 border border-emerald-200 rounded-xl shadow-sm space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-emerald-800">Quick Add Company</p>
                  <button type="button" onClick={() => setShowQuickCompanyForm(false)} className="text-emerald-500 hover:text-emerald-700 cursor-pointer">✕</button>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="label text-xs font-semibold text-emerald-900">Company Name <span className="text-red-500">*</span></label>
                    {quickCompanyName.trim() && (
                      <span className="text-[11px] font-mono font-semibold text-emerald-700 bg-emerald-100/80 px-2 py-0.5 rounded border border-emerald-200">
                        Auto Code: {generateSmartShortCodeCandidates(quickCompanyName).find((c) => !companies.some((comp) => comp.short_code.toUpperCase() === c.toUpperCase())) || 'NEW'}
                      </span>
                    )}
                  </div>
                  <input
                    type="text"
                    value={quickCompanyName}
                    onChange={(e) => setQuickCompanyName(e.target.value)}
                    placeholder="Enter Company Name (e.g. Swastik Private Limited)"
                    className="input bg-white text-xs"
                    autoFocus
                  />
                </div>
                <div className="flex justify-end gap-2 pt-1">
                  <button type="button" onClick={() => setShowQuickCompanyForm(false)} className="btn btn-secondary text-xs cursor-pointer">Cancel</button>
                  <button type="button" onClick={handleQuickCreateCompany} disabled={quickCompanySaving} className="btn btn-primary text-xs cursor-pointer">
                    {quickCompanySaving ? 'Creating…' : 'Create & Select'}
                  </button>
                </div>
              </div>
            )}

            {/* Inline Personnel Form for Competent Person */}
            {showInlineInspectorForm && (
              <div className="p-4 bg-brand-50/90 border border-brand-200 rounded-xl shadow-sm space-y-3">
                <InlinePersonnelForm
                  initialRole="competent_person"
                  initialFullName={activeInspectorInitialName}
                  title="Create New Competent Person / Inspector"
                  onCreated={handleInspectorCreated}
                  onCancel={() => {
                    setShowInlineInspectorForm(false);
                    setActiveInspectorTargetRowId(null);
                    setActiveInspectorInitialName('');
                  }}
                />
              </div>
            )}

            {/* Step 1: Upload File area if no parsed rows yet */}
            {parsedRows.length === 0 ? (
              <>
                <div className="border-2 border-dashed border-slate-300 hover:border-brand-500 rounded-2xl p-8 text-center transition-colors bg-white">
                  <input
                    type="file"
                    accept=".xlsx, .xls, .csv"
                    onChange={handleFileUpload}
                    id="form11-excel-file-upload-input"
                    className="hidden"
                  />
                  <label htmlFor="form11-excel-file-upload-input" className="cursor-pointer block space-y-3">
                    <div className="w-14 h-14 bg-brand-50 text-brand-600 rounded-2xl flex items-center justify-center mx-auto text-2xl shadow-sm">
                      📁
                    </div>
                    <div>
                      <span className="text-base font-bold text-slate-800 hover:text-brand-600 underline">
                        Click to browse & upload Excel file
                      </span>
                      <span className="text-slate-500 text-sm block mt-1">or drag and drop `.xlsx` / `.csv` file here</span>
                    </div>
                    <p className="text-xs text-slate-400">
                      Supports any Excel sheet (matches Company Short Code or Name, Vessel Type & Inspector Name automatically!)
                    </p>
                  </label>
                  {parsing && (
                    <p className="text-xs font-semibold text-brand-600 mt-4 flex items-center justify-center gap-2">
                      <span className="animate-spin h-4 w-4 border-2 border-brand-600 border-t-transparent rounded-full" />
                      Parsing Excel spreadsheet data...
                    </p>
                  )}
                </div>

                <div className="flex items-center justify-between pt-3 border-t border-slate-200">
                  <div className="text-xs text-slate-500 font-medium">
                    Upload Excel file to begin
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      handleResetModal();
                      onClose();
                    }}
                    className="btn btn-secondary text-xs cursor-pointer"
                  >
                    Cancel
                  </button>
                </div>
              </>
            ) : (
              /* Step 2: Interactive Table & Review */
              <>
                <div className="space-y-3">
                  {/* File Info Bar */}
                  <div className="p-3 bg-slate-50 border border-slate-200 rounded-xl flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <span className="w-7 h-7 bg-brand-100 text-brand-700 rounded-lg flex items-center justify-center font-bold text-xs shrink-0">📄</span>
                      <div>
                        <span className="font-bold text-slate-900 text-sm">
                          Parsed {parsedRows.length} Row(s) from <span className="text-brand-700 font-mono">{fileName}</span>
                        </span>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={handleResetModal}
                      className="px-3 py-1 bg-white border border-slate-200 text-slate-600 hover:text-slate-900 rounded-lg text-xs font-semibold hover:bg-slate-50 shadow-sm transition-all cursor-pointer"
                    >
                      Upload Different File
                    </button>
                  </div>

                  {/* Batch Actions Toolbar */}
                  <div className="p-3 bg-white border border-slate-200 rounded-xl shadow-sm flex flex-wrap items-center gap-3 text-xs">
                    <span className="font-bold text-slate-800 shrink-0 flex items-center gap-1.5">
                      <span className="text-amber-500 text-sm">⚡</span>
                      <span>Batch Apply:</span>
                    </span>

                    {/* Batch Company */}
                    <div className="flex items-center gap-1.5 min-w-[240px]">
                      <div className="flex-1">
                        <AsyncSearchableSelect
                          value={batchCompanyId}
                          onChange={(val) => setBatchCompanyId(val)}
                          fetchOptions={fetchCompanyOptions}
                          placeholder="Select Company…"
                          isCompanySelect={true}
                        />
                      </div>
                      <button
                        type="button"
                        onClick={applyBatchCompany}
                        disabled={!batchCompanyId}
                        className="px-3 h-8 bg-slate-800 hover:bg-slate-900 text-white rounded-lg text-xs font-semibold disabled:opacity-40 transition-all shadow-sm shrink-0 whitespace-nowrap inline-flex items-center justify-center cursor-pointer"
                      >
                        Apply All
                      </button>
                    </div>

                    {/* Batch Vessel Name / Type */}
                    <div className="flex items-center gap-1.5 min-w-[220px]">
                      <select
                        value={batchVesselName}
                        onChange={(e) => setBatchVesselName(e.target.value)}
                        className="input text-xs bg-slate-50 border-slate-300 py-1 px-2.5 h-8 font-medium flex-1"
                      >
                        {COMMON_VESSEL_TYPES.map((v) => (
                          <option key={v} value={v}>
                            {v}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={applyBatchVessel}
                        disabled={!batchVesselName}
                        className="px-3 h-8 bg-slate-800 hover:bg-slate-900 text-white rounded-lg text-xs font-semibold disabled:opacity-40 transition-all shadow-sm shrink-0 whitespace-nowrap inline-flex items-center justify-center cursor-pointer"
                      >
                        Apply All
                      </button>
                    </div>

                    {/* Batch Inspector */}
                    <div className="flex items-center gap-1.5">
                      <select
                        value={batchPersonnelId}
                        onChange={(e) => setBatchPersonnelId(e.target.value)}
                        className="input text-xs bg-slate-50 border-slate-300 py-1 px-2.5 h-8 font-medium max-w-[170px]"
                      >
                        <option value="">-- Inspector --</option>
                        {inspectors.map((ins) => (
                          <option key={ins.id} value={ins.id}>
                            {ins.full_name}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={applyBatchPersonnel}
                        disabled={!batchPersonnelId}
                        className="px-3 h-8 bg-slate-800 hover:bg-slate-900 text-white rounded-lg text-xs font-semibold disabled:opacity-40 transition-all shadow-sm shrink-0 whitespace-nowrap inline-flex items-center justify-center cursor-pointer"
                      >
                        Apply All
                      </button>
                    </div>

                    {/* Batch Date */}
                    <div className="flex items-center gap-1.5">
                      <input
                        type="date"
                        value={batchDate}
                        onChange={(e) => setBatchDate(e.target.value)}
                        className="input text-xs bg-slate-50 border-slate-300 py-1 px-2.5 h-8 font-medium"
                      />
                      <button
                        type="button"
                        onClick={applyBatchDate}
                        disabled={!batchDate}
                        className="px-3 h-8 bg-slate-800 hover:bg-slate-900 text-white rounded-lg text-xs font-semibold disabled:opacity-40 transition-all shadow-sm shrink-0 whitespace-nowrap inline-flex items-center justify-center cursor-pointer"
                      >
                        Apply Date
                      </button>
                    </div>

                    {/* Batch Working Pressure */}
                    <div className="flex items-center gap-1.5">
                      <input
                        type="text"
                        value={batchPressure}
                        onChange={(e) => setBatchPressure(e.target.value)}
                        placeholder="e.g. 6"
                        className="input text-xs bg-slate-50 border-slate-300 py-1 px-2 h-8 font-medium w-16 text-center"
                      />
                      <button
                        type="button"
                        onClick={applyBatchPressure}
                        disabled={!batchPressure}
                        className="px-3 h-8 bg-slate-800 hover:bg-slate-900 text-white rounded-lg text-xs font-semibold disabled:opacity-40 transition-all shadow-sm shrink-0 whitespace-nowrap inline-flex items-center justify-center cursor-pointer"
                      >
                        Apply Pressure
                      </button>
                    </div>

                    {/* Batch Condition */}
                    <div className="flex items-center gap-1.5">
                      <select
                        value={batchCondition}
                        onChange={(e) => setBatchCondition(e.target.value)}
                        className="input text-xs bg-slate-50 border-slate-300 py-1 px-2.5 h-8 font-medium"
                      >
                        {CONDITION_OPTIONS.map((c) => (
                          <option key={c} value={c}>
                            {c}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={applyBatchCondition}
                        className="px-3 h-8 bg-slate-800 hover:bg-slate-900 text-white rounded-lg text-xs font-semibold transition-all shadow-sm shrink-0 whitespace-nowrap inline-flex items-center justify-center cursor-pointer"
                      >
                        Apply Condition
                      </button>
                    </div>
                  </div>

                  {/* Warning Banner for Multiple Companies matching same name */}
                  {multipleMatchRowsCount > 0 && (
                    <div className="p-3.5 bg-amber-50 border border-amber-300 text-amber-950 rounded-xl text-xs flex items-start gap-3 shadow-sm">
                      <span className="text-amber-600 font-bold text-lg shrink-0">⚠️</span>
                      <div className="space-y-1">
                        <h4 className="font-bold text-amber-900 text-xs sm:text-sm">
                          Friendly Note: Multiple Companies Found with Matching Name ({multipleMatchRowsCount} Row{multipleMatchRowsCount > 1 ? 's' : ''})
                        </h4>
                        <p className="text-amber-800 text-[11px] sm:text-xs">
                          The company name in your Excel matches <strong>multiple companies</strong> in the system for {multipleMatchRowsCount} row{multipleMatchRowsCount > 1 ? 's' : ''}.
                          The first match was auto-selected. <strong>Please validate that the selected company is correct before proceeding.</strong>
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Validation Info Banner */}
                  {!validationSummary.isValid && (
                    <div className="p-3 bg-amber-50 border border-amber-300 text-amber-900 rounded-xl text-xs flex items-center justify-between">
                      <span className="font-semibold">⚠️ Action Required: {validationSummary.reason}</span>
                      <span className="text-[11px] text-amber-700">Satisfy all mandatory fields to activate import button</span>
                    </div>
                  )}

                  {/* Interactive Staging Table */}
                  <div className="max-h-[50vh] overflow-y-auto overflow-x-auto border border-slate-200 rounded-xl shadow-inner bg-white">
                    <table className="w-full text-left text-xs border-collapse">
                      <thead className="sticky top-0 bg-slate-100 border-b border-slate-200 z-10 font-bold text-slate-700">
                        <tr>
                          <th className="py-2.5 px-3 w-10 text-center">#</th>
                          <th className="py-2.5 px-3 min-w-[280px]">Company Selection <span className="text-red-500">*</span></th>
                          <th className="py-2.5 px-3 min-w-[220px]">Vessel Name / Description <span className="text-red-500">*</span></th>
                          <th className="py-2.5 px-3 min-w-[130px]">Tag / Serial No</th>
                          <th className="py-2.5 px-3 min-w-[130px]">SWP (Kg/cm²) <span className="text-red-500">*</span></th>
                          <th className="py-2.5 px-3 min-w-[130px]">Hydro Pressure</th>
                          <th className="py-2.5 px-3 min-w-[120px]">Capacity</th>
                          <th className="py-2.5 px-3 min-w-[150px]">Location</th>
                          <th className="py-2.5 px-3 min-w-[200px]">Competent Person <span className="text-red-500">*</span></th>
                          <th className="py-2.5 px-3 min-w-[130px]">Exam Date <span className="text-red-500">*</span></th>
                          <th className="py-2.5 px-3 min-w-[110px]">Condition</th>
                          <th className="py-2.5 px-3 min-w-[150px]">Status & Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {parsedRows.map((row, idx) => (
                          <tr key={row.id} className="hover:bg-slate-50/80 transition-colors">
                            <td className="py-2 px-3 text-center text-slate-400 font-mono text-[11px]">{idx + 1}</td>

                            {/* Company Dropdown + + New Company */}
                            <td className="py-2 px-3 space-y-1">
                              <div className="flex items-center gap-1">
                                <div className="flex-1">
                                  <AsyncSearchableSelect
                                    value={row.company_id}
                                    onChange={(val) => updateRow(row.id, { company_id: val })}
                                    fetchOptions={fetchCompanyOptions}
                                    placeholder="Search company…"
                                    isCompanySelect={true}
                                  />
                                </div>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setActiveCompanyTargetRowId(row.id);
                                    setQuickCompanyName(row.company_input || '');
                                    setShowQuickCompanyForm(true);
                                  }}
                                  className="px-1.5 py-1 text-[11px] font-bold text-emerald-700 hover:text-emerald-900 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded shrink-0 cursor-pointer"
                                  title="Create New Company"
                                >
                                  + New Company
                                </button>
                              </div>
                              {row.has_multiple_company_matches ? (
                                <p className="text-[10px] text-amber-900 font-semibold bg-amber-50/90 p-1.5 rounded border border-amber-300 mt-1">
                                  ⚠️ <strong>Multiple Companies Found ({row.matching_companies_count} matches):</strong> Matched {row.matching_company_names?.join(', ')}. Please validate that this is the intended company.
                                </p>
                              ) : !row.company_id && row.company_input ? (
                                <p className="text-[10px] text-amber-700 font-medium">
                                  Unmatched Excel Company: "{row.company_input}" — Click + New Company to create
                                </p>
                              ) : null}
                            </td>

                            {/* Vessel Name / Description */}
                            <td className="py-2 px-3 space-y-1">
                              <input
                                type="text"
                                list="common-vessel-types-datalist"
                                value={row.vessel_name_description}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  updateRow(row.id, {
                                    vessel_name_description: val,
                                    vessel_serial_no: row.vessel_serial_no || computeDefaultVesselSerial(val, idx + 1),
                                  });
                                }}
                                className={`input text-xs py-1 px-2 font-medium w-full ${!row.vessel_name_description ? 'border-amber-300 bg-amber-50/50' : 'bg-white'}`}
                                placeholder="e.g. Vertical Air Receiver"
                              />
                              <datalist id="common-vessel-types-datalist">
                                {COMMON_VESSEL_TYPES.map((v) => (
                                  <option key={v} value={v} />
                                ))}
                              </datalist>
                              {!row.vessel_name_description && row.vessel_input && (
                                <p className="text-[10px] text-amber-700 font-medium">
                                  Unmatched Vessel: "{row.vessel_input}"
                                </p>
                              )}
                            </td>

                            {/* Tag / Serial No */}
                            <td className="py-2 px-3">
                              <input
                                type="text"
                                value={row.vessel_serial_no}
                                onChange={(e) => updateRow(row.id, { vessel_serial_no: e.target.value })}
                                className="input text-xs py-1 px-2 font-mono uppercase bg-white w-full"
                                placeholder="VART/01"
                              />
                            </td>

                            {/* Safe Working Pressure (SWP) */}
                            <td className="py-2 px-3">
                              <input
                                type="text"
                                value={row.safe_working_pressure_recommended}
                                onChange={(e) => updateRow(row.id, { safe_working_pressure_recommended: e.target.value })}
                                className="input text-xs py-1 px-2 bg-white w-full font-medium"
                                placeholder="6 Kg/cm²"
                              />
                            </td>

                            {/* Hydro Test Pressure */}
                            <td className="py-2 px-3">
                              <input
                                type="text"
                                value={row.hydro_test_pressure}
                                onChange={(e) => updateRow(row.id, { hydro_test_pressure: e.target.value })}
                                className="input text-xs py-1 px-2 bg-white w-full font-medium text-slate-700"
                                placeholder="9 Kg/cm²"
                              />
                            </td>

                            {/* Capacity */}
                            <td className="py-2 px-3">
                              <input
                                type="text"
                                value={row.vessel_capacity}
                                onChange={(e) => updateRow(row.id, { vessel_capacity: e.target.value })}
                                className="input text-xs py-1 px-2 bg-white w-full"
                                placeholder="1 KL"
                              />
                            </td>

                            {/* Location */}
                            <td className="py-2 px-3">
                              <input
                                type="text"
                                value={row.vessel_location}
                                onChange={(e) => updateRow(row.id, { vessel_location: e.target.value })}
                                className="input text-xs py-1 px-2 bg-white w-full"
                                placeholder="Near Compressor Room"
                              />
                            </td>

                            {/* Competent Person Dropdown + + New Inspector */}
                            <td className="py-2 px-3 space-y-1">
                              <div className="flex items-center gap-1">
                                <select
                                  value={row.personnel_id}
                                  onChange={(e) => updateRow(row.id, { personnel_id: e.target.value })}
                                  className={`input text-xs py-1 px-2 font-medium flex-1 ${!row.personnel_id ? 'border-amber-300 bg-amber-50/50' : 'bg-white'}`}
                                >
                                  <option value="">-- Select Inspector --</option>
                                  {inspectors.map((ins) => (
                                    <option key={ins.id} value={ins.id}>
                                      {ins.full_name}
                                    </option>
                                  ))}
                                </select>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setActiveInspectorTargetRowId(row.id);
                                    setActiveInspectorInitialName(row.personnel_input || '');
                                    setShowInlineInspectorForm(true);
                                  }}
                                  className="px-1.5 py-1 text-[11px] font-bold text-brand-700 hover:text-brand-900 bg-brand-50 hover:bg-brand-100 border border-brand-200 rounded shrink-0 cursor-pointer"
                                  title="Create New Inspector"
                                >
                                  + New Inspector
                                </button>
                              </div>
                              {!row.personnel_id && row.personnel_input && (
                                <p className="text-[10px] text-amber-700 font-medium">
                                  Unmatched Inspector: "{row.personnel_input}" — Click + New Inspector to create
                                </p>
                              )}
                            </td>

                            {/* Exam Date */}
                            <td className="py-2 px-3">
                              <input
                                type="date"
                                value={row.examination_date}
                                onChange={(e) => updateRow(row.id, { examination_date: e.target.value })}
                                className="input text-xs py-1 px-1.5 bg-white font-medium"
                                required
                              />
                            </td>

                            {/* Condition */}
                            <td className="py-2 px-3">
                              <select
                                value={row.condition_vessel}
                                onChange={(e) =>
                                  updateRow(row.id, {
                                    condition_vessel: e.target.value,
                                    condition_piping: e.target.value,
                                    fittings_safety_valve: e.target.value,
                                    fittings_pressure_gauges: e.target.value,
                                  })
                                }
                                className={`input text-xs py-1 px-1.5 font-medium ${row.condition_vessel === 'Not Good' ? 'text-red-700 font-bold bg-red-50' : 'bg-white'}`}
                              >
                                {CONDITION_OPTIONS.map((c) => (
                                  <option key={c} value={c}>
                                    {c}
                                  </option>
                                ))}
                              </select>
                            </td>

                            {/* Status & Actions */}
                            <td className="py-2 px-3">
                              <div className="flex items-center justify-between gap-1 flex-wrap">
                                {row.has_multiple_company_matches && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-900 border border-amber-300" title="Multiple companies found with this name. Please validate that the correct company is selected.">
                                    ⚠️ Please Validate
                                  </span>
                                )}
                                {row.status === 'VALID' && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-100 text-emerald-800">
                                    ✓ Ready
                                  </span>
                                )}
                                {row.status === 'MISSING_COMPANY' && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-amber-100 text-amber-800" title={row.status_message}>
                                    ⚠️ {row.status_message}
                                  </span>
                                )}
                                {row.status === 'MISSING_INSPECTOR' && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-amber-100 text-amber-800" title={row.status_message}>
                                    ⚠️ {row.status_message}
                                  </span>
                                )}
                                {row.status === 'MISSING_VESSEL' && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-amber-100 text-amber-800" title={row.status_message}>
                                    ⚠️ {row.status_message}
                                  </span>
                                )}
                                {row.status === 'INVALID' && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-red-100 text-red-800" title={row.status_message}>
                                    ⚠️ {row.status_message}
                                  </span>
                                )}
                                <button
                                  type="button"
                                  onClick={() => removeRow(row.id)}
                                  className="text-slate-400 hover:text-red-600 font-bold p-1 cursor-pointer"
                                  title="Remove Row"
                                >
                                  ✕
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Progress Bar during execution */}
                  {progress && (
                    <div className="p-4 bg-brand-50 border border-brand-200 rounded-xl space-y-2">
                      <div className="flex items-center justify-between text-xs font-bold text-brand-900">
                        <span>{progress.step}</span>
                        <span>{progress.current} / {progress.total}</span>
                      </div>
                      <div className="w-full bg-brand-200 rounded-full h-2.5 overflow-hidden">
                        <div
                          className="bg-brand-600 h-2.5 rounded-full transition-all duration-200"
                          style={{ width: `${Math.round((progress.current / progress.total) * 100)}%` }}
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Bottom Modal Actions Bar */}
                <div className="flex items-center justify-between pt-3 border-t border-slate-200">
                  <div className="text-xs text-slate-500 font-medium flex items-center gap-2">
                    <span>{parsedRows.length > 0 ? `${parsedRows.length} certificate(s) ready` : 'Upload Excel file to begin'}</span>
                    {!validationSummary.isValid && parsedRows.length > 0 && (
                      <span className="text-amber-700 font-semibold bg-amber-50 px-2 py-0.5 rounded border border-amber-200 text-[11px]">
                        ⚠️ {validationSummary.reason}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        handleResetModal();
                        onClose();
                      }}
                      disabled={importing}
                      className="btn btn-secondary text-xs"
                    >
                      Cancel
                    </button>
                    {parsedRows.length > 0 && (
                      <button
                        type="button"
                        onClick={handleExecuteImport}
                        disabled={importing || !validationSummary.isValid}
                        className={`btn text-xs px-5 py-2 flex items-center gap-1.5 font-semibold transition-all ${
                          validationSummary.isValid && !importing
                            ? 'bg-brand-600 hover:bg-brand-700 text-white shadow-md cursor-pointer'
                            : 'bg-slate-300 text-slate-500 cursor-not-allowed opacity-70'
                        }`}
                        title={!validationSummary.isValid ? validationSummary.reason : 'Import Form 11 certificates'}
                      >
                        {importing ? (
                          <>
                            <span className="animate-spin h-3.5 w-3.5 border-2 border-white border-t-transparent rounded-full" />
                            Importing Certificates…
                          </>
                        ) : (
                          <>
                            🚀 Confirm & Import {parsedRows.length} Form 11 Certificates
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}
